import React, { useRef } from "react";
import { Message } from "../../interfaces/chat.interface";
import { ConversationType, ConversationTypeDescription } from "../../constants/chat.constant";
import { NAVIGATION_ITEMS } from "@/constants/navigation.constant";
import ChatInput, { ChatInputRef } from "./chat-input";
import chopLogo from "@/assets/chop_logo.svg";

/**
 * Props for the ChatSuggestions component
 */
interface ChatSuggestionsProps {
  /** Array of suggestion prompts for desktop */
  suggestions: Array<{ prompt: string }>;
  /** Array of suggestion prompts for mobile */
  mobileSuggestions: Array<{ prompt: string }>;
  /** Type of conversation */
  conversationType: ConversationType;
  /** Callback fired when a suggestion is selected */
  onSend: (message: Message) => void;
  /** Current input content */
  content: string;
  /** Current conversation messages */
  displayConversation: Message[] | null;
  /** Whether a response is being generated */
  isResponseGenerating: boolean;
  /** Selected AI model */
  selectedModel: string;
  /** Model temperature setting */
  modelTemperature: number;
  /** Model reasoning effort setting */
  modelEffort: number;
  /** Callback fired when model changes */
  onModelChange: (model: string) => void;
  /** Callback fired when temperature changes */
  onTemperatureChange: (temperature: number) => void;
  /** Callback fired when reasoning effort changes */
  onReasoningEffortChange: (effort: number) => void;
  /** Callback fired when typing starts */
  onStartTyping: () => void;
  /** Callback fired when typing stops */
  onStopTyping: () => void;
  /** Callback fired when input changes */
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Callback fired on keyboard events */
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Callback fired when message is sent */
  onSendMessage: (message: Message) => void;
  /** Whether sensitive chat mode is active */
  isSensitive?: boolean;
  /** Callback fired when sensitive mode is toggled */
  onToggleSensitive?: () => void;
  /** Whether sensitive mode toggle is disabled */
  isSensitiveDisabled?: boolean;
  /** Callback fired to cancel streaming */
  onCancelStream: () => void;
  /** Ref to the chat input component */
  chatInputRef?: React.RefObject<ChatInputRef> | React.ForwardedRef<ChatInputRef>;
}

/**
 * ChatSuggestions component displays the initial chat interface with logo, suggestion
 * prompts, and chat input. Shows different suggestions for mobile and desktop. When
 * a suggestion is clicked, it populates the input field and focuses it.
 * 
 * @component
 * @param {ChatSuggestionsProps} props - Component props
 * @param {Array<{prompt: string}>} props.suggestions - Desktop suggestions
 * @param {Array<{prompt: string}>} props.mobileSuggestions - Mobile suggestions
 * @param {ConversationType} props.conversationType - Conversation type
 * @param {(message: Message) => void} props.onSend - Send callback
 * @param {string} props.content - Input content
 * @param {Message[] | null} props.displayConversation - Conversation messages
 * @param {boolean} props.isResponseGenerating - Whether generating response
 * @param {string} props.selectedModel - Selected model
 * @param {number} props.modelTemperature - Temperature setting
 * @param {number} props.modelEffort - Reasoning effort setting
 * @param {(model: string) => void} props.onModelChange - Model change callback
 * @param {(temperature: number) => void} props.onTemperatureChange - Temperature change callback
 * @param {(effort: number) => void} props.onReasoningEffortChange - Effort change callback
 * @param {() => void} props.onStartTyping - Start typing callback
 * @param {() => void} props.onStopTyping - Stop typing callback
 * @param {(e: React.ChangeEvent<HTMLTextAreaElement>) => void} props.onInputChange - Input change callback
 * @param {(e: React.KeyboardEvent<HTMLTextAreaElement>) => void} props.onKeyDown - Key down callback
 * @param {(message: Message) => void} props.onSendMessage - Send message callback
 * @param {boolean} [props.isSensitive] - Sensitive mode flag
 * @param {() => void} [props.onToggleSensitive] - Toggle sensitive callback
 * @param {boolean} [props.isSensitiveDisabled] - Sensitive disabled flag
 * @param {() => void} props.onCancelStream - Cancel stream callback
 * @param {React.RefObject<ChatInputRef> | React.ForwardedRef<ChatInputRef>} [props.chatInputRef] - Chat input ref
 * @returns {JSX.Element} Chat suggestions interface with logo, prompts, and input
 * 
 * @example
 * ```tsx
 * <ChatSuggestions
 *   suggestions={desktopSuggestions}
 *   mobileSuggestions={mobileSuggestions}
 *   onSend={handleSend}
 *   content={content}
 * />
 * ```
 */
export const ChatSuggestions: React.FC<ChatSuggestionsProps> = ({
  suggestions,
  mobileSuggestions,
  conversationType,
  onSend,
  content,
  displayConversation,
  isResponseGenerating,
  selectedModel,
  modelEffort,
  modelTemperature,
  onModelChange,
  onTemperatureChange,
  onReasoningEffortChange,
  onStartTyping,
  onStopTyping,
  onInputChange,
  onKeyDown,
  onSendMessage,
  isSensitive = false,
  onToggleSensitive,
  isSensitiveDisabled = false,
  onCancelStream,
  chatInputRef: externalRef
}) => {
  const internalRef = useRef<ChatInputRef>(null);
  const chatInputRef = (externalRef && typeof externalRef === 'object' && 'current' in externalRef)
    ? externalRef as React.RefObject<ChatInputRef>
    : internalRef;

  const activeNavItem = NAVIGATION_ITEMS.find((item) => item.type === conversationType);

  const isMobile = () => {
    const userAgent =
      typeof window.navigator === "undefined" ? "" : navigator.userAgent;
    const mobileRegex =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
    return mobileRegex.test(userAgent);
  };

  const handleSuggestionClick = (prompt: string) => {
    const syntheticEvent = {
      target: { value: prompt },
    } as React.ChangeEvent<HTMLTextAreaElement>;

    onInputChange(syntheticEvent);

    setTimeout(() => {
      chatInputRef.current?.focusAtEnd();
    }, 0);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo container */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 md:justify-end md:pb-8">
        <div className="w-[280px] md:w-[300px] lg:w-[340px] md:h-auto h-[50%] max-h-[100px] mb-6 md:mb-8">
          <img
            src={chopLogo}
            alt="Children's Hospital of Philadelphia"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Tool heading */}
      {activeNavItem && (
        <div className="flex items-center justify-center gap-3 pb-8">
          <span className={`inline-flex items-center justify-center h-10 w-10 rounded ${activeNavItem.iconColor ?? ''}`}>
            <activeNavItem.icon className="h-6 w-6 text-white" />
          </span>
          <span className="text-4xl font-semibold text-primary-600">{activeNavItem.title}</span>
        </div>
      )}

      {/* Tool description */}
      {activeNavItem && ConversationTypeDescription[conversationType] && (
        <div className="flex justify-center px-4 pb-6">
          <p className="text-sm text-primary-600 text-center w-full md:max-w-[56rem]">
            {ConversationTypeDescription[conversationType]}
          </p>
        </div>
      )}

      {/* Mobile suggestions */}
      <div className="mx-4 flex flex-col items-center px-4 pb-6 md:hidden">
        <div className="w-full max-w-xl">
          <div className="text-primary-500 text-xs mb-4 text-center font-medium">
            Get started with a few suggestions
          </div>

          <div className="flex flex-col gap-3 w-full [@media(max-height:800px)]:h-[calc(60svh-194px)] overflow-y-auto">
            {mobileSuggestions.map((value, index) => (
              <span
                key={index}
                onClick={() => handleSuggestionClick(value.prompt)}
                className="text-sm [@media(max-height:670px)]:text-xs text-left border border-gray-300 rounded-lg p-4 text-primary-500 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
              >
                {value.prompt}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Input area */}
      <div className="px-4 pb-4 md:flex md:justify-center sticky bottom-[0px]">
        <div className="w-full md:max-w-[56rem]">
          <ChatInput
            ref={chatInputRef}
            activeConversation={displayConversation}
            content={content}
            mobile={isMobile()}
            onStartTyping={onStartTyping}
            onStopTyping={onStopTyping}
            onInputChange={onInputChange}
            onKeyDown={onKeyDown}
            onSend={onSendMessage}
            isResponseGenerating={isResponseGenerating}
            onModelChange={onModelChange}
            selectedModel={selectedModel}
            temperature={modelTemperature}
            effort={modelEffort}
            onTemperatureChange={onTemperatureChange}
            onReasoningEffortChange={onReasoningEffortChange}
            onCancelStream={onCancelStream}
            conversationType={conversationType}
          />
        </div>
      </div>

      {/* Desktop suggestions */}
      <div className="hidden md:flex flex-1 flex-col items-center justify-start pb-2">
        <div className="flex flex-col items-center lg:max-w-[56rem] font-semibold">
          <div className="text-primary-500 text-xs mb-4">
            Get started with a few suggestions
          </div>

          <div className="suggestions p-1 flex flex-wrap justify-center items-center w-full">
            {suggestions.map((value, index) => (
              <span
                key={index}
                onClick={() => handleSuggestionClick(value.prompt)}
                className="text-xs ml-3 mt-3 justify-center bg-white gap-3 border border-gray-200 p-3 text-primary-500 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className="line-clamp-2 break-word text-left w-full overflow-ellipsis">
                  {value.prompt}
                </div>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
